import { Injectable } from '@angular/core';
import { NavigationEnd, PRIMARY_OUTLET, Router, UrlSegment } from '@angular/router';
import { Contact } from '@app/contacts/models/contact';
import { DbWorkerService } from '@app/core/db/db-worker.service';
import { Response } from '@app/core/models/api.models';
import { EventName } from '@app/core/models/event-bus.models';
import { SnackBarType } from '@app/core/models/snack-bar-data.model';
import { ApiService } from '@app/core/services/api.service';
import { EventBusService } from '@app/core/services/event-bus.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { ElectronService } from '@app/electron/electron.service';
import { NotificationKeyBackend } from '@app/preferences/models/settings.models';
import { NotificationService } from '@app/preferences/services/notification.service';
import { environment } from '@environment/environment';
import { TranslateService } from '@ngx-translate/core';
import { ElectronInvocationChannel } from '@shared/types';
import { saveAs } from 'file-saver-es';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  tap,
} from 'rxjs';

import { FaxMessageDataSource } from '../data-sources/fax-message-data-source';
import {
  AllLocalFaxNumber,
  CoverPageTemplate,
  FaxDeletedMessage,
  FaxesDeletedMessage,
  FaxLinks,
  FaxMessage,
  FaxMessageResponse,
  FaxUrlResponse,
  ListFaxCoverPageApiTemplatesResponse,
  ListFaxNumbersResponse,
  LocalFaxNumber,
  MAX_FAX_FILE_SIZE_BYTES,
} from '../models/fax.models';
import { generateCoverPageHtml } from '../utils/cover-page.util';
import { FaxMessageStorageService } from './fax-message-storage.service';

export const DEFAULT_FAX_CALLER_ID = '13214246840';

@Injectable({
  providedIn: 'root',
})
export class FaxService extends ApiService {
  protected path = '';

  public sendingSubject = new BehaviorSubject<boolean>(false);
  public sending$ = this.sendingSubject.asObservable();

  public readonly loadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this.loadingSubject.asObservable();

  public readonly hasErrorSubject = new BehaviorSubject<boolean>(false);
  public readonly hasError$ = this.hasErrorSubject.asObservable();

  public readonly localFaxNumbersSubject = new BehaviorSubject<LocalFaxNumber[] | null>(null);
  public readonly localFaxNumbers$ = this.localFaxNumbersSubject.asObservable();
  public readonly hasLocalFaxNumbersSubject = new BehaviorSubject<boolean>(false);
  public readonly hasLocalFaxNumbers$ = this.hasLocalFaxNumbersSubject.asObservable();

  /**
   * Every time our URL changes (i.e. when we receive a `NavigationEnd` event),
   * update the selectedLocalNumber based on whatever is in the url. If the url contains
   * a number that doesn't belong to the user, set the value to undefined.
   */
  public readonly selectedFaxLocalNumberSubject = new BehaviorSubject<LocalFaxNumber | null>(null);
  public readonly selectedFaxLocalNumber$ = this.selectedFaxLocalNumberSubject.asObservable();

  private readonly coverPageTemplatesSubject = new BehaviorSubject<CoverPageTemplate[]>([]);
  public readonly coverPageTemplates$ = this.coverPageTemplatesSubject.asObservable();

  /**
   * Emits unread fax counts whenever the in-memory cache changes.
   * Replaces the old data$ observable — consumers should use this for badge/count logic.
   */
  public readonly unreadCounts$ = this.faxMessageStorageService.bulkDataChanged$.pipe(
    startWith(undefined),
    map(() => this.faxMessageStorageService.getUnreadCounts())
  );

  // LRU cache for PDF blobs with size-based limit to prevent unbounded memory growth.
  // Intentionally larger than MAX_FAX_FILE_SIZE_BYTES so a single large fax doesn't evict the whole cache.
  private static readonly MAX_CACHE_SIZE_BYTES = 200 * 1024 * 1024;
  public faxBlobPdfData: Map<string, Blob> = new Map();
  private readonly pdfAccessTimes: Map<string, number> = new Map();
  private readonly pdfSizes: Map<string, number> = new Map();

  private currentUrl: string = '';
  private faxLinks: Map<FaxMessage['direction'], FaxLinks> = new Map();

  // ========== Getters / Setters ==========

  public get allLocalNumber(): LocalFaxNumber {
    return AllLocalFaxNumber;
  }

  // ========== Lifecycle ===========

  constructor(
    private webSocketService: WebSocketService,
    eventBusService: EventBusService,
    private router: Router,
    private snackbar: SnackbarService,
    private notificationService: NotificationService,
    private storage: LocalStorageService,
    private electronService: ElectronService,
    private translateService: TranslateService,
    public readonly faxMessageStorageService: FaxMessageStorageService,
    private readonly dbWorker: DbWorkerService
  ) {
    super(`${environment.faxHubGateway}/{me}/faxes`);

    this.dbWorker.seeded.then((result) => {
      this.faxMessageStorageService.seedInMemory(result.faxes);
    });

    eventBusService.on(EventName.Logout).subscribe(() => {
      this.cleanupOnLogout();
    });

    const selectedFaxLocalNumberStorageKey = 'selectedFaxLocalNumberStorageKey';
    const lastStoredLocalNumber = this.storage.get<LocalFaxNumber>(selectedFaxLocalNumberStorageKey);
    if (lastStoredLocalNumber) {
      this.selectedFaxLocalNumberSubject.next(lastStoredLocalNumber);
    }

    // Always persist the selectedLocalNumber to localStorage
    this.selectedFaxLocalNumber$.subscribe((value) => {
      if (value === null) {
        this.storage.delete(selectedFaxLocalNumberStorageKey);
      } else {
        this.storage.set(selectedFaxLocalNumberStorageKey, value);
      }
    });

    this.localFaxNumbers$.subscribe((localNumbers) => {
      const hasLocalNumbers = (localNumbers && localNumbers.length > 0) || false;
      this.hasLocalFaxNumbersSubject.next(hasLocalNumbers);
    });

    // Observe outbound messages - WebSocket → DexieDB
    this.webSocketService.on('FaxSent', (message: FaxMessage) => {
      this.processFaxMessage(message, 'outbound');
    });

    // Observe inbound messages - WebSocket → DexieDB
    this.webSocketService.on('FaxReceived', (message: FaxMessage) => {
      this.processFaxMessage(message, 'inbound');
      this.snackbar.open(this.translateService.instant('FAX.NEW_FAX_RECEIVED') as string);
      this.notificationService.showNotification(
        {
          title: this.translateService.instant('FAX.NEW_FAX_RECEIVED') as string,
          body: message.contact?.fullName
            ? (this.translateService.instant('FAX.RECEIVED_FROM', { name: message.contact.fullName }) as string)
            : '',
        },
        NotificationKeyBackend['Incoming Fax']
      );
    });

    // Mark as read - WebSocket → in-memory cache (cacheChangedSubject drives DataSource refresh)
    this.webSocketService.on('FaxMarkAsRead', (message: { id: string }) => {
      this.faxMessageStorageService.markAsRead(String(message.id), true);
    });

    // Delete single fax - WebSocket → in-memory cache
    this.webSocketService.on('FaxDeleted', (message: FaxDeletedMessage) => {
      const id = String(message.id);
      this.faxMessageStorageService.delete(id);
      this.removeFaxFromCache(id);
    });

    // Delete multiple faxes - WebSocket → in-memory cache
    this.webSocketService.on('FaxesDeleted', (message: FaxesDeletedMessage) => {
      const ids = message.ids.map(String);
      this.faxMessageStorageService.bulkDelete(ids);
      for (const id of ids) {
        this.removeFaxFromCache(id);
      }
    });

    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd)
      )
      .subscribe((navigationEndEvent) => {
        this.currentUrl = navigationEndEvent.url;
        this.setLocalNumberFromUrl();
      });
  }

  private getFaxes(direction: FaxMessage['direction']): Observable<FaxMessage[]> {
    if (this.faxLinks.get(direction)?.next === null) {
      return of([]);
    }
    this.loadingSubject.next(true);
    const link = this.generateFaxLink(direction);
    return this.get<FaxMessageResponse>(`${link}`).pipe(
      catchError(() => {
        return of({ data: [], links: { next: null } });
      }),

      map((faxMessages) => {
        this.faxLinks.set(direction, faxMessages.links);
        return faxMessages.data.map((faxMessage: FaxMessage) => ({
          ...faxMessage,
          id: String(faxMessage.id),
          direction: direction,
        }));
      }),
      tap((messages) => {
        this.faxMessageStorageService.upsertItems(messages);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  private generateFaxLink(direction: FaxMessage['direction']) {
    return this.faxLinks.has(direction) ? `${direction}${this.faxLinks.get(direction)?.next}` : `${direction}`;
  }

  private getLocalNumbers(): Observable<ListFaxNumbersResponse> {
    return this.get<ListFaxNumbersResponse>(`numberlist`);
  }

  private getCoverTemplates(): Observable<ListFaxCoverPageApiTemplatesResponse> {
    return this.get<ListFaxCoverPageApiTemplatesResponse>(`templates`);
  }

  private async loadCoverPageTemplates(): Promise<void> {
    try {
      const { templates } = await firstValueFrom(this.getCoverTemplates());
      this.coverPageTemplatesSubject.next(templates);
    } catch (error) {
      console.error('[FAX] Failed to load cover page templates:', error);
    }
  }

  public init(): void | Error {
    // Initialize the service
    // Note: Data fetching is done separately via refreshData()
  }

  /**
   * Fetch fax data from API and store in DexieDB.
   * Similar to SMS refreshData pattern: API → DexieDB → UI (via liveQuery)
   */
  public refreshData(): Observable<void> {
    this.loadingSubject.next(true);
    this.hasErrorSubject.next(false);
    // Reset pagination links to force fresh data fetch on reconnection
    this.faxLinks.clear();

    this.loadCoverPageTemplates();

    return forkJoin([this.getFaxes('inbound'), this.getFaxes('outbound'), this.getLocalNumbers()]).pipe(
      tap(([_inboundFaxes, _outboundFaxes, localNumbers]) => {
        this.localFaxNumbersSubject.next(localNumbers.data);
        this.setLocalNumberFromUrl();
      }),
      catchError((_error) => {
        this.hasErrorSubject.next(true);
        return of(void 0);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Create a new DataSource for fax messages.
   * This is the recommended way to display fax messages in a list - it handles
   * pagination efficiently and only loads what's visible, preventing memory issues.
   */
  public createDataSource(): FaxMessageDataSource {
    return new FaxMessageDataSource(this.faxMessageStorageService);
  }

  private setLocalNumberFromUrl() {
    const tree = this.router.parseUrl(this.currentUrl);
    const urlSegments: UrlSegment[] | undefined = tree.root.children[PRIMARY_OUTLET]?.segments;
    if (!urlSegments) {
      return;
    }

    const faxSegmentIndex = urlSegments.findIndex((segment) => segment.path === 'fax');
    if (faxSegmentIndex !== -1 && faxSegmentIndex + 1 < urlSegments.length) {
      const numberInPath = urlSegments[faxSegmentIndex + 1];
      const localNumber = [this.allLocalNumber, ...(this.localFaxNumbersSubject.value || [])].find(
        (number) => number.number === numberInPath.path
      );
      console.log(`fax: setting local number with value ${localNumber?.number}`);
      this.selectedFaxLocalNumberSubject.next(localNumber || this.allLocalNumber);
    }
  }

  private processFaxMessage(message: FaxMessage, direction: 'inbound' | 'outbound'): void {
    const normalizedFaxMessage: FaxMessage = {
      ...message,
      id: String(message.id),
      direction: direction,
      unread: direction === 'inbound',
    };

    this.faxMessageStorageService.upsertItems([normalizedFaxMessage]);
  }

  public loadFaxMessages(tabSelected: number): void {
    const direction: FaxMessage['direction'] = tabSelected === 0 ? 'inbound' : 'outbound';
    this.getFaxes(direction).subscribe();
  }

  public loadNextPage(direction: FaxMessage['direction']): Observable<FaxMessage[]> {
    return this.getFaxes(direction);
  }

  public async markAsRead(message: FaxMessage): Promise<void> {
    try {
      await firstValueFrom(this.put<Response>(`${message.id}/read`, {}));
      this.faxMessageStorageService.markAsRead(message.id, true);
    } catch (error) {
      console.error('Failed to mark fax as read: ', error);
    }
  }

  private getThumbnail(id: string) {
    return this.get<Blob>(`${id}/thumbnail`, {
      responseType: 'blob',
    }).pipe(
      tap((response) => {
        return response;
      })
    );
  }

  public async downloadData(id: string, type: 'confirmation' | 'pdf', url?: string): Promise<Blob> {
    const cacheKey = `${id}-${type}`;

    // Check if already cached and update access time
    if (this.faxBlobPdfData.has(cacheKey)) {
      this.pdfAccessTimes.set(cacheKey, Date.now());
      return this.faxBlobPdfData.get(cacheKey)!;
    }

    let response: Blob;
    /* use new endpoint to download fax data if url is defined. Fallback to old one in case of error */
    if (url) {
      try {
        response = await firstValueFrom(this.get(url, { responseType: 'blob' }));
      } catch {
        response = await firstValueFrom(this.get(`${id}/${type}`, { responseType: 'blob' }));
      }
    } else {
      response = await firstValueFrom(this.get(`${id}/${type}`, { responseType: 'blob' }));
    }

    if (response) {
      const blobSize = response.size;

      // Evict LRU PDFs if adding this blob would exceed cache size limit
      this.evictToMakeRoom(blobSize);

      // Cache the blob
      this.faxBlobPdfData.set(cacheKey, response);
      this.pdfAccessTimes.set(cacheKey, Date.now());
      this.pdfSizes.set(cacheKey, blobSize);

      console.log(
        `[FAX] Cached ${cacheKey}: ${(blobSize / 1024 / 1024).toFixed(2)}MB (total: ${(this.getTotalCacheSize() / 1024 / 1024).toFixed(2)}MB / ${(FaxService.MAX_CACHE_SIZE_BYTES / 1024 / 1024).toFixed(0)}MB)`
      );
    }
    return response;
  }

  public async downloadFax(id: string, url?: string) {
    try {
      const pdfData = await this.downloadData(id, 'pdf', url);
      saveAs(pdfData, `fax-${id}.pdf`);
    } catch (error) {
      console.warn('Failed to download fax: ', error);
    }
  }

  public async downloadConfirmation(id: string, url?: string) {
    try {
      const pdfData = await this.downloadData(id, 'confirmation', url);
      saveAs(pdfData, `fax-confirmation${id}.pdf`);
    } catch (error) {
      console.warn('Failed to download fax confirmation: ', error);
    }
  }

  public deleteFax(id: string) {
    return this.post<FaxDeleteResponse>(`${id}/delete`, {}).pipe(
      tap((response) => {
        if (response.success) {
          this.faxMessageStorageService.delete(id);
          this.removeFaxFromCache(id);
        } else {
          console.warn(response.message);
        }
      })
    );
  }

  public async printFax(id: string, url?: string): Promise<void> {
    try {
      const pdfData = await this.downloadData(id, 'pdf', url);

      if (this.electronService.isRunningInElectron) {
        // In Electron, save PDF to Downloads folder and open with default viewer
        try {
          const pdfArrayBuffer = await pdfData.arrayBuffer();
          const pdfUint8Array = new Uint8Array(pdfArrayBuffer);

          const success = await this.electronService.invoke(ElectronInvocationChannel.SaveAndOpenPdf, {
            pdfData: pdfUint8Array,
            fileName: `fax-${id}.pdf`,
          });

          if (!success) {
            console.warn('Failed to save or open PDF');
            this.snackbar.open(this.translateService.instant('FAX.FAILED_OPEN_PDF') as string, undefined, {
              panelClass: SnackBarType.ERROR,
            });
          }
        } catch (ipcError) {
          // Check if the error is due to missing handler (older Electron version)
          if (ipcError.name === 'ElectronInvocationError') {
            console.warn('PDF viewing handler not available in this Electron version:', ipcError);
            this.snackbar.open(this.translateService.instant('FAX.PDF_VIEWER_UPDATE_REQUIRED') as string, undefined, {
              panelClass: SnackBarType.WARNING,
              duration: 8000,
            });

            // Fall back to download behavior
            saveAs(pdfData, `fax-${id}.pdf`);
          } else {
            // Some other error occurred
            console.error('Error invoking Electron PDF handler:', ipcError);
            this.snackbar.open(this.translateService.instant('FAX.FAILED_OPEN_PDF') as string, undefined, {
              panelClass: SnackBarType.ERROR,
            });
          }
        }
      } else {
        // In browser, keep original behavior - open in new window
        const pdfFile = new Blob([pdfData], { type: 'application/pdf' });
        window.open(URL.createObjectURL(pdfFile), '');
      }
    } catch (error) {
      console.warn('Failed to print fax: ', error);
    }
  }

  async addFaxUrl(file: File): Promise<FaxUrlResponse> {
    try {
      const response = await firstValueFrom(
        this.post<FaxUrlResponse>(`${environment.faxHubGateway}/{me}/upload/url`, {
          filename: file.name,
          type: 'fax',
          access: 'private',
        })
      );
      await firstValueFrom(this.put(response.url, file));
      return response;
    } catch (error) {
      if (error.message) {
        this.snackbar.open(error.message, undefined, { panelClass: SnackBarType.ERROR });
      }
      throw new Error('failed to add fax url');
    }
  }

  sendFax(formData: FormData): Observable<object | null> {
    this.sendingSubject.next(true);
    return this.post<FormData>('send', formData).pipe(finalize(() => this.sendingSubject.next(false)));
  }

  // ========== List item mutation helpers ==========

  /**
   * Update read status for a fax message.
   */
  public async updateRead(id: string, state: boolean): Promise<void> {
    this.faxMessageStorageService.markAsRead(id, state);
  }

  /**
   * Remove a fax message from storage and cache.
   */
  public async removeItem(id: string): Promise<void> {
    this.faxMessageStorageService.delete(id);
    this.removeFaxFromCache(id);
  }

  /**
   * Update contact field for a fax message (lazy update pattern).
   * Called by fax-list-item when it looks up a contact for display.
   * This populates the contact field in IndexedDB so search by contact name works.
   */
  public async updateFaxContact(id: string, contact: Contact): Promise<void> {
    this.faxMessageStorageService.updateItem(id, { contact });
  }

  // ========== Helpers ==========
  public isAllLocalNumber(number: string | LocalFaxNumber | null | undefined) {
    if (!number) {
      return false;
    }

    return typeof number === 'string'
      ? number === this.allLocalNumber.number
      : number.number === this.allLocalNumber.number;
  }

  isAllDataLoaded(direction: FaxMessage['direction']) {
    return this.faxLinks.get(direction)?.next === null;
  }

  /**
   * Returns true if the file is within the allowed fax file size limit.
   * If it exceeds the limit, shows a snackbar warning and returns false.
   */
  public validateFaxFileSize(file: File): boolean {
    if (file.size > MAX_FAX_FILE_SIZE_BYTES) {
      this.snackbar.open(
        this.translateService.instant('FAX.FILE_TOO_LARGE', {
          maxSize: MAX_FAX_FILE_SIZE_BYTES / 1024 / 1024,
        }) as string,
        this.translateService.instant('FAX.OK') as string,
        { duration: 4000 }
      );
      return false;
    }
    return true;
  }

  // ========== LRU Cache Management for PDF Blobs ==========

  /**
   * Calculate total cache size from pdfSizes Map.
   * Single source of truth - eliminates sync issues.
   */
  private getTotalCacheSize(): number {
    return Array.from(this.pdfSizes.values()).reduce((sum, size) => sum + size, 0);
  }

  /**
   * Remove a cached blob and update size tracking.
   */
  private removeCachedBlob(cacheKey: string): void {
    if (this.faxBlobPdfData.has(cacheKey)) {
      this.faxBlobPdfData.delete(cacheKey);
      this.pdfAccessTimes.delete(cacheKey);
      this.pdfSizes.delete(cacheKey);
    }
  }

  /**
   * Remove all cached blobs for a fax message (pdf, confirmation, thumbnail).
   */
  private removeFaxFromCache(id: string): void {
    this.removeCachedBlob(`${id}-pdf`);
    this.removeCachedBlob(`${id}-confirmation`);
    this.removeCachedBlob(`${id}-thumbnail`);
  }

  /**
   * Evict least recently used PDFs to make room for a new blob.
   * Continues evicting until there's enough space for the incoming blob.
   * Prevents unbounded memory growth from cached PDF blobs.
   */
  private evictToMakeRoom(incomingBlobSize: number): void {
    // Calculate once and track locally for O(1) performance in hot path
    let currentSize = this.getTotalCacheSize();

    // Check if we need to evict
    while (currentSize + incomingBlobSize > FaxService.MAX_CACHE_SIZE_BYTES && this.faxBlobPdfData.size > 0) {
      // Find the least recently accessed PDF
      let oldestKey: string | undefined;
      let oldestTime = Date.now();

      for (const [key, accessTime] of this.pdfAccessTimes.entries()) {
        if (accessTime < oldestTime) {
          oldestTime = accessTime;
          oldestKey = key;
        }
      }

      if (oldestKey) {
        const evictedSize = this.pdfSizes.get(oldestKey) || 0;
        this.removeCachedBlob(oldestKey);
        currentSize -= evictedSize; // Update local tracker

        console.log(
          `[FAX] Evicted ${oldestKey}: ${(evictedSize / 1024 / 1024).toFixed(2)}MB (cache now: ${(currentSize / 1024 / 1024).toFixed(2)}MB)`
        );
      } else {
        // Safety: break if we can't find anything to evict
        break;
      }
    }
  }

  /**
   * Comprehensive cleanup on logout to prevent memory leaks and stale state.
   * Follows the same pattern as SMS service (CUC-3608).
   */
  private cleanupOnLogout(): void {
    console.log('[FAX] Cleaning up on logout');

    // Clear PDF blob cache and access times
    this.faxBlobPdfData.clear();
    this.pdfAccessTimes.clear();
    this.pdfSizes.clear();
    console.log('[FAX] Cleared PDF cache');

    // Clear pagination links
    this.faxLinks.clear();

    // Reset state subjects
    this.localFaxNumbersSubject.next(null);
    this.selectedFaxLocalNumberSubject.next(null);
    this.coverPageTemplatesSubject.next([]);
    this.loadingSubject.next(false);
    this.hasErrorSubject.next(false);

    // Clear IndexedDB storage
    this.faxMessageStorageService.drop().catch((error) => {
      console.error('[FAX] Error dropping fax storage on logout:', error);
    });

    console.log('[FAX] Logout cleanup complete');
  }

  // ========== Cover Page Template Methods ==========

  public async saveCoverPageTemplate(formData: CoverPageTemplate, templateId?: number): Promise<CoverPageTemplate> {
    const isCreating = !templateId;

    if (isCreating) {
      // Create via API then re-fetch to get the server-assigned id
      await this.createCoverPageTemplate(formData);
      const { templates } = await firstValueFrom(this.getCoverTemplates());
      this.coverPageTemplatesSubject.next(templates);
    } else {
      // Update via API then update the subject directly
      await this.updateCoverPageTemplate(templateId, formData);
      const updated = this.coverPageTemplatesSubject.value.map((t) =>
        t.id === templateId ? { ...formData, id: templateId } : t
      );
      this.coverPageTemplatesSubject.next(updated);
    }

    return formData;
  }

  private async createCoverPageTemplate(template: CoverPageTemplate): Promise<Response> {
    const apiFormData = await this.buildCoverPageTemplateFormData(template);
    return firstValueFrom(this.post<Response>('template', apiFormData));
  }

  private async updateCoverPageTemplate(id: number, template: CoverPageTemplate): Promise<Response> {
    const apiFormData = await this.buildCoverPageTemplateFormData(template);
    return firstValueFrom(this.post<Response>(`template/${id}`, apiFormData));
  }

  private async buildCoverPageTemplateFormData(template: CoverPageTemplate): Promise<FormData> {
    const apiFormData = new FormData();
    apiFormData.append('name', template.name);
    apiFormData.append('top_text', template.top_text);
    apiFormData.append('organization_name', template.organization_name);
    apiFormData.append('organization_address', template.organization_address);
    apiFormData.append('phone_number', template.phone_number);
    apiFormData.append('website', template.website);
    apiFormData.append('bottom_text', template.bottom_text);

    if (template.logo_url?.startsWith('data:')) {
      const logoBlob = this.base64ToBlob(template.logo_url);
      apiFormData.append('logo', logoBlob, 'logo.png');
    } else if (template.logo_url) {
      const logoBlob = await firstValueFrom(this.get<Blob>(template.logo_url, { responseType: 'blob' }));
      apiFormData.append('logo', logoBlob, 'logo.png');
    }
    apiFormData.append('template_html', generateCoverPageHtml(template, true));

    return apiFormData;
  }

  private base64ToBlob(base64: string): Blob {
    const [header, data] = base64.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const byteString = atob(data);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: mime });
  }

  public async deleteCoverPageTemplate(id: number): Promise<void> {
    await firstValueFrom(this.delete<Response>(`template/${id}`));
    this.coverPageTemplatesSubject.next(this.coverPageTemplatesSubject.value.filter((t) => t.id !== id));
  }

  // ========== Cover Page HTML Generation ==========

  public async uploadCoverPageFromTemplate(template: CoverPageTemplate): Promise<FaxUrlResponse> {
    const html = generateCoverPageHtml(template, true);
    const blob = new Blob([html], { type: 'text/html' });
    const file = new File([blob], 'cover-page.html', { type: 'text/html' });
    return await this.addFaxUrl(file);
  }
}

class FaxDeleteResponse {
  success: boolean;
  message: string;
}
